import { Component, Input, OnInit } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { FlowType, Proofreading } from 'src/app/models/proofreading.model';
import { AlertService } from 'src/app/services/alert.service';
import { LoaderService } from 'src/app/services/loader.service';
import { DashboardHistoryService } from 'src/app/services/dashboard-history.service';
import { LivecoAPIService } from 'src/app/services/liveco-api.service';
import { IChildProject } from 'src/app/models/projects/project.model';
import { StateMachineService } from 'src/app/services/machine-state.service';
import { ProofreadingService } from 'src/app/services/proofreading.service';
import { ProjectService } from 'src/app/services/project.service';
import { ViewComponent } from 'src/app/shared/pages/view/view.component';
import { AuthService } from 'src/app/services/auth.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { SelectDialog, SelectDialogOptions } from 'src/app/shared/components/dialog/select-dialog/select-dialog.component';
import { DashboardHistoryDetailComponent } from '../dashboard-history-detail/dashboard-history-detail.component';
import { TranslateService } from '@ngx-translate/core';
import { IMember, Member, MemberPermission } from 'src/app/models/projects/submodels/members.model';
import { IProofreadingMember } from 'src/app/models/proofreading-member.model';
import { ExternStateName } from 'src/app/models/states.model';


interface IThumbnail {
    src: string;
}
@Component({
    selector: '[app-project-child-card]',
    templateUrl: './project-child-card.component.html',
    styleUrls: ['./project-child-card.component.scss'],
})
export class ProjectsChildCardsComponent extends ViewComponent implements OnInit {

    @Input() project: IChildProject;
    ExternPayloadFlag: any;

    get archived() {
        return this.project.state === 'archived';
    }


    get projectID() {
        return this.project._id;
    }

    get noInternProofreafing() {
        return this.proofreadings.filter(pr => pr.flow === FlowType.PDF_REPO);
    }


    get proofreadings() {
        return this.project.custom.proofreadings?.sort((a, b) => Object.keys(FlowType).indexOf(a.flow) - Object.keys(FlowType).indexOf(b.flow)) || [];
    }

    get available_flows() {
        return this.project.custom.proofreadings?.map((pr: Proofreading) => pr.flow) || [];
    }

    get flowType() {
        return FlowType;
    }

    private _thumbnail: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    get thumbnail(): Observable<string | null> {
        return this._thumbnail.asObservable();
    }

    constructor(
        protected route: ActivatedRoute,
        protected router: Router,
        protected s_alert: AlertService,
        protected loaderService: LoaderService,
        protected s_auth: AuthService,
        protected dialog: MatDialog,
        protected s_dashboardHistory: DashboardHistoryService,
        protected liveco: LivecoAPIService,
        protected s_sm: StateMachineService,
        protected s_proofreadings: ProofreadingService,
        protected s_projects: ProjectService,
        
        protected s_loader: LoaderService
    ) {
        super(route, router, s_alert, s_loader, s_auth);
    }

    ngOnInit(): void {
        const isOld = this.project?.custom?.general?.is_old;
        this.subscription.add(this.liveco.get$(this.project.identifier, '/project/thumbnail',undefined, { is_old: isOld  }).subscribe((thumb: IThumbnail | null) => {
            if (thumb?.src) {
            this._thumbnail.next(thumb.src);
            } else {
            this._thumbnail.next('/assets/thumbnail-default.jpg');
            }
        }));
    }

    getPrefixRelecture(flow: FlowType) {
        return ProofreadingService.getPrefixRelecture(flow);
    }

    openFolderChild(childJob: any) {
        this.s_projects.openFolderChild(childJob);
    }

    openFolderOld(childJob: any) {
        this.s_projects.openFolderOld(childJob);
    }

    filterProjectStorage(project: IChildProject) {
        this.s_dashboardHistory.updateProjectCard(project);
    }

    filterKpi(project: IChildProject) {

        this.router.navigate(['/stats'], { queryParams: { brand: project?.custom?.general_info?.marque, identifier: project?.identifier } });

    }

    getFeedBackClient(proofreading: Proofreading) {
        const members: IProofreadingMember[] = proofreading.members;
        const memberWithApproval = members.find(member => member.permissions.includes(MemberPermission.APPROVAL));
        return memberWithApproval && proofreading.state.slug === ExternStateName.FEEDBACK_CLIENT ? memberWithApproval.status : '';
    }

    openCreateProofreadingModal(project: IChildProject) {
        const options: SelectDialogOptions = {
            title: 'SELECT_FLOW',
            valid: 'OK',
            data: Object.values(FlowType),
            defaultValue: [FlowType.EXE],
            label: 'FLOW',
            multiple: true,
        };
        this.dialog
            .open(SelectDialog, {
                panelClass: 'dialog',
                data: options,
                disableClose: true,
                width: '450px'
            })
            .afterClosed()
            .subscribe(async (response: any) => {
                const { data } = response;
                for (const flow of data) {
                    await this.s_proofreadings.create(new Proofreading({
                        project: {
                            ID: project._id,
                            identifier: project.identifier
                        },
                        flow
                    }));
                }
                this.s_projects.reloadEntities();
            });
    }

    openProjectHistory() {
        let mode = "extern";

        if (this.isAdmin() || this.isCppOrFab() || this.isOperator()) {
            mode = "intern";
        }

        this.dialog
            .open(DashboardHistoryDetailComponent, {
                width: '80rem',
                data: {
                    projectID: this.project._id,
                    mode
                }
            });
    }

    copyToClipboard(text: string | string[]) {
        if (_.isArray(text)) {
            text = text.join('\n');
        }
        navigator.clipboard.writeText(text).then(() => {
            this.s_alert.success('TEXT.COPIED_IDENTIFIER');
        });
    }

    getSlugText(next: string | undefined, intern: boolean, proofreading: Proofreading): string | null {
        if (!next) {
            return null;
        }
        const next_state = this.s_sm.entities.find(e => e.slug === next && e.type === (intern ? 'intern' : 'extern') && e.flow.includes(proofreading.flow));
        return next_state?.payload?.text_slug || next;
    }

    getFileName(proofreading: Proofreading): { fileName: string, shortFileName: string; } | undefined {
        const fileNameFromPath = (path: string): string => path.split('/').pop()?.replace(/%20/g, ' ') || '';
        const fileNameWithExtension = fileNameFromPath(proofreading.file_url || '');
        const [fileName, extension] = fileNameWithExtension.split(/(?=\.[^.]+$)/);
        const shortFileName = fileName.length > 20 ? `${fileName.slice(0, 20)}...${extension}` : fileNameWithExtension;
        return fileName ? { fileName: fileNameWithExtension, shortFileName } : undefined;
        
    }

    dislayInternPr(proofreading: Proofreading) {
        return proofreading.flow !== FlowType.PDF_REPO && proofreading.flow !== FlowType.EZD_PDF_REPOSITORY && proofreading.flow !== FlowType.BAT;
    }

    displayPrStatus(proofreading: Proofreading) {
        return proofreading.flow !== FlowType.BAT;
    }

}